import React from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
;
import { Button, Columns, Column } from 'src/ui/mui-ui-kit';;
import { reduceServerErrors, setBreadcrumbs } from 'src/ui';
import { getToken } from 'src/auth';
import { getUser, fetchLoggedInUser } from 'src/user';
import {
  acknowledgeBackgroundAssessment,
  resolveBackgroundAssessment,
  addBackgroundAssessmentLog,
  fetchBackgroundAssessmentLogTypes,
  ComplianceDetailPage,
  ComplianceHistory,
  getBackgroundAssessmentLog,
} from 'src/compliance';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { getAccounts, fetchAccounts, fetchAccount } from 'src/account';
import { ComplianceDashboard } from '../components/ComplianceDashboard';
import { createAccountArray } from '../utils';
import { UiMultiSelect } from '../../ui';

const DASHBOARD = 'Dashboard';
const OVERVIEW = 'Overview';
const reportTypes = [DASHBOARD, OVERVIEW];

const InnerComplianceDashboardPage = ({
  user,
  accountArray,
  backgroundAssessmentLog,
}) => {
  const location = useLocation();
  const [report, setReport] = React.useState(DASHBOARD);
  const [shouldFetchHistory, setShouldFetchHistory] = React.useState(false);
  const accountIds = React.useMemo(() => accountArray.map(a => a.value), [
    accountArray,
  ]);
  const now = new Date();
  const today = moment(
    new Date(now.getFullYear(), now.getMonth(), now.getDate())
  );
  const startDate = moment(`${now.getFullYear()}-01-01`, 'YYYY-MM-DD'); // Year to date
  const endDate = today
    .clone()
    .add(1, 'days')
    .subtract(1, 'seconds');
  const [userFilters, setUserFilters] = React.useState({
    accounts: accountIds,
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),
  });

  // Update accounts filter when accountIds changes (e.g., after accounts load)
  React.useEffect(() => {
    if (accountIds.length > 0 && userFilters.accounts.length === 0) {
      setUserFilters(prev => ({ ...prev, accounts: accountIds }));
    }
  }, [accountIds]);

  const onDateRangeChange = (start, end) => {
    setShouldFetchHistory(false);
    setUserFilters({
      ...userFilters,
      startDate: start,
      endDate: end,
    });
  };

  return (
    <>
      <Routes location={location}>
        <Route
          path="/"
          element={(
            <div className="compliance-dashboard-page">
              <Columns>
                <Column>
                  <div className="compliance-dashboard-page-controls">
                    <div style={{ display: 'flex' }}>
                      <div className="select">
                        <select
                          value={report}
                          onChange={e => setReport(e.target.value)}
                        >
                          {reportTypes.map(type => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>{' '}
                      <div className="date-range-picker" style={{ display: 'flex' }}>
                        <DatePicker
                          selected={userFilters.startDate}
                          onChange={date => onDateRangeChange(date, userFilters.endDate)}
                          selectsStart
                          startDate={userFilters.startDate}
                          endDate={userFilters.endDate}
                          maxDate={userFilters.endDate}
                          className="input"
                          wrapperClassName="date-picker-wrapper"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          minDate={new Date(2020, 0, 1)}
                        />
                        <DatePicker
                          selected={userFilters.endDate}
                          onChange={date => onDateRangeChange(userFilters.startDate, date)}
                          selectsEnd
                          startDate={userFilters.startDate}
                          endDate={userFilters.endDate}
                          minDate={userFilters.startDate}
                          maxDate={new Date(now.getFullYear() + 1, 11, 31)}
                          className="input"
                          wrapperClassName="date-picker-wrapper"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                      </div>
                      {report === OVERVIEW && (
                        <Button
                          onClick={() => setShouldFetchHistory(true)}
                          isColor="gradient-bright"
                        >
                          <span>Fetch data</span>
                        </Button>
                      )}
                    </div>
                    <UiMultiSelect
                      options={accountArray}
                      style={{ width: 400 }}
                      placeholder="Reporting accounts*"
                      onChange={accounts =>
                        setUserFilters({ ...userFilters, accounts })
                      }
                      value={userFilters.accounts}
                      requireOneSelected
                    />
                  </div>
                </Column>
              </Columns>
              <Columns>
                <Column>
                  {report === OVERVIEW && !shouldFetchHistory && (
                    <div
                      style={{
                        fontStyle: 'italic',
                        marginLeft: 3,
                        opacity: 0.85,
                      }}
                    >
                      Press &quot;Fetch data&quot; button above to generate
                      report. It may take a moment.
                    </div>
                  )}
                </Column>
              </Columns>
              {report === DASHBOARD && (
                <ComplianceDashboard
                  user={user}
                  location={location}
                  userFilters={userFilters}
                  backgroundAssessmentLog={backgroundAssessmentLog}
                />
              )}
              {report === OVERVIEW && (
                <ComplianceHistory
                  user={user}
                  userFilters={userFilters}
                  shouldFetchHistory={shouldFetchHistory}
                  backgroundAssessmentLog={backgroundAssessmentLog}
                />
              )}
            </div>
          )}
        />
        <Route
          path="background_assessments/edit/:id"
          element={(() => {
            const fromDashboard = location.state?.from === 'dashboard';
            return (
              <ComplianceDetailPage
                addBackgroundAssessmentLog={addBackgroundAssessmentLog}
                fromDashboard={fromDashboard}
              />
            );
          })()}
        />
      </Routes>
    </>
  );
};

const OuterComplianceDashboardPage = ({
  token,
  user,
  accounts,
  setBreadcrumbs,
  backgroundAssessmentLog,
  fetchAccount,
  fetchAccounts,
  fetchLoggedInUser,
  fetchBackgroundAssessmentLogTypes,
}) => {
  const location = useLocation();
  React.useEffect(() => {
    (async () => {
      setBreadcrumbs([{ label: 'Compliance', href: '/compliance' }]);
      if (token.isValid()) {
        await fetchLoggedInUser();
        if (user.financialInstitutionId) {
          if (user.isMerchantAdmin) {
            if (user.accountId == null) {
              throw new Error('User has no account');
            }
            await fetchAccount(user.accountId);
          } else {
            await fetchAccounts({
              financialInstitutionId: user.financialInstitutionId,
              userId: user.id,
            });
          }
          if (accounts.isError()) {
            reduceServerErrors(accounts);
          }
        }
      }
      await fetchBackgroundAssessmentLogTypes();
    })();
  }, []);

  if (!accounts.isError() && accounts.items) {
    const accountChoices = user.accountId
      ? accounts.items.filter(a => a.id === user.accountId)
      : accounts.items;
    return (
      <InnerComplianceDashboardPage
        user={user}
        accountArray={createAccountArray(accountChoices)}
        backgroundAssessmentLog={backgroundAssessmentLog}
        addBackgroundAssessmentLog={addBackgroundAssessmentLog}
      />
    );
  }
  return null;
};

const mapStateToProps = state => ({
  token: getToken(state),
  user: getUser(state),
  accounts: getAccounts(state),
  backgroundAssessmentLog: getBackgroundAssessmentLog(state),
});

const ConnectedComplianceDashboard = connect(mapStateToProps, {
  fetchLoggedInUser,
  setBreadcrumbs,
  acknowledgeBackgroundAssessment,
  resolveBackgroundAssessment,
  fetchBackgroundAssessmentLogTypes,
  fetchAccount,
  fetchAccounts,
})(OuterComplianceDashboardPage);

export function ComplianceDashboardPage() {
  return <ConnectedComplianceDashboard />;
}

InnerComplianceDashboardPage.propTypes = {
  user: propTypes.object.isRequired,
  accountArray: propTypes.array.isRequired,
  backgroundAssessmentLog: propTypes.object.isRequired,
  addBackgroundAssessmentLog: propTypes.func.isRequired,
};

OuterComplianceDashboardPage.propTypes = {
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  accounts: propTypes.object.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  backgroundAssessmentLog: propTypes.object.isRequired,
  fetchAccount: propTypes.func.isRequired,
  fetchAccounts: propTypes.func.isRequired,
  fetchLoggedInUser: propTypes.func.isRequired,
  fetchBackgroundAssessmentLogTypes: propTypes.func.isRequired,
};

ComplianceDashboardPage.propTypes = {};
